import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AppService } from '../../../app.service';
import { AdminGridDirective, AdminGridConfig, AdminGridCallbackEvent } from '../../shared/admin-grid.directive';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';
import { AdminFilterEditComponent } from './admin-filter-edit.component';

/**
 * Angular 12 migration of admin.admin-filters.
 * Grid: loadGridJson?action_id=1102&grid_id=230
 * Filter bar: refresh, "Add New Filter", client dropdown.
 * Grid callback / "Add New Filter": opens AdminFilterEditComponent via MatDialog.
 */
@Component({
    selector: 'gtw-admin-filters',
    templateUrl: './admin-filters.component.html',
    styleUrls: ['./admin-filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminFiltersComponent implements OnInit, OnDestroy {
  @ViewChild(AdminGridDirective) adminGrid!: AdminGridDirective;

  gridConfig!: AdminGridConfig;
  loading = false;
  clients: any[] = [];
  selectedClientKey: any = '';

  private readonly ACTION_ID = 1102;
  private readonly GRID_ID = '230';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private dialog: MatDialog,
    private adminScreenContext: AdminScreenContextService,
  ) {}

  ngOnInit(): void {
    this.setupClients();
    this.gridConfig = {
      actionId: this.ACTION_ID,
      gridId: this.GRID_ID,
      extraParams: { client_key: this.selectedClientKey },
      autoLoad: true,
      cacheKey: 'admin/admin-filters',
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // Grid events
  // ---------------------------------------------------------------------------

  onGridCallback(event: AdminGridCallbackEvent): void {
    const rowData = event.data?.rowData ?? event.data;
    this.openEditModal(rowData);
  }

  onGridDataLoaded(_data: any): void {
    this.cdr.detectChanges();
  }

  refresh(): void {
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh({ client_key: this.selectedClientKey });
  }

  onClientChange(): void {
    this.gridConfig = {
      ...this.gridConfig,
      extraParams: { client_key: this.selectedClientKey },
    };
    this.adminGrid?.clearCache();
    this.adminGrid?.refresh({ client_key: this.selectedClientKey });
  }

  // ---------------------------------------------------------------------------
  // Modal — Add / Edit Filter Group
  // ---------------------------------------------------------------------------

  addScreen(): void {
    this.openEditModal(null);
  }

  private openEditModal(rowData: any): void {
    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '90vw';
    config.disableClose = true;
    config.data = {
      rowData: rowData || {},
      mode: rowData?.FILTER_GROUP_KEY ? 'edit' : 'new',
      // Pass the client_key currently selected in the filter bar so the modal
      // saves under the right client. Without this the modal would fall back
      // to USER_SETTINGS.user.client_key (the user's *default* client) and
      // silently write the new filter to the wrong client.
      selectedClientKey: this.selectedClientKey,
    };

    this.adminScreenContext.setRowData(rowData || {});
    const ref = this.dialog.open(AdminFilterEditComponent, config);
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.adminScreenContext.setRowData({});
        if (result === 'saved') {
          this.adminGrid?.clearCache();
          this.adminGrid?.refresh({ client_key: this.selectedClientKey });
        }
      });
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private setupClients(): void {
    const userClients = this.appService.USER_SETTINGS?.user?.clients;
    const userClient = this.appService.USER_SETTINGS?.user?.client;
    if (Array.isArray(userClients) && userClients.length > 0) {
      this.clients = userClients;
    } else if (userClient?.client_key !== undefined) {
      this.clients = [userClient];
    }
    if (this.clients.length > 0) {
      this.selectedClientKey = this.clients[0].client_key;
    }
  }
}
